// ─── Address Autocomplete Field ───────────────────────────────────────────────
customElements.get('address-autocomplete-field') || customElements.define('address-autocomplete-field',
  class extends HTMLElement {
    constructor() { super() }

    connectedCallback() {
      this.initAutocomplete()

      const submitBtn = this.querySelector('.address-autocomplete-field__submit-button')
      if (submitBtn) {
        submitBtn.addEventListener('click', () => this.handleButtonClick())
      }

      this.querySelector('input')?.addEventListener('focus', () => this.handleRemoveErrorMessage())
    }

    initAutocomplete() {
      const input = this.querySelector('#autocomplete')
      if (!input || input.disabled) return  // skip the read-only display input

      const autocomplete = new google.maps.places.Autocomplete(input, {
        types: ['street_address', 'premise'],
        componentRestrictions: { country: ['US'] },
        fields: ['geometry', 'formatted_address']
      })

      autocomplete.addListener('place_changed', () => this.onPlaceChanged(autocomplete))
    }

    showLoader() {
      this.querySelector('.loading-overlay__spinner')?.classList.remove('hidden')
      this.querySelector('button')?.classList.add('ls-button--text-hidden')
    }

    hideLoader() {
      this.querySelector('.loading-overlay__spinner')?.classList.add('hidden')
      this.querySelector('button')?.classList.remove('ls-button--text-hidden')
    }

    onPlaceChanged = (autocomplete) => {
      const place = autocomplete.getPlace()

      if (!place.geometry) {
        this.querySelector('#autocomplete').placeholder = 'Enter your home address'
        return
      }

      const lat = place.geometry.location.lat()
      const lng = place.geometry.location.lng()
      const address = place.formatted_address

      sessionStorage.setItem('ls-lat', lat)
      sessionStorage.setItem('ls-lng', lng)
      sessionStorage.setItem('ls-address', address)

      // Fire event so calculate-yard-size can react
      document.dispatchEvent(new CustomEvent('placeChanged', {
        detail: { lat, lng, address }
      }))
    }

    handleRemoveErrorMessage() {
      this.querySelector('#address-autocomplete-field__error-message')?.remove()
    }

    handleErrorMessage() {
      if (this.querySelector('#address-autocomplete-field__error-message')) return

      const div = document.createElement('div')
      div.id = 'address-autocomplete-field__error-message'
      div.textContent = 'Please type your address in the field below and select an address from the dropdown. 👇'
      this.insertAdjacentElement('afterbegin', div)
    }

    handleButtonClick() {
      this.showLoader()

      if (sessionStorage.getItem('ls-lat')) {
        // Same page — just emit the event with stored coords
        document.dispatchEvent(new CustomEvent('placeChanged', {
          detail: {
            lat: parseFloat(sessionStorage.getItem('ls-lat')),
            lng: parseFloat(sessionStorage.getItem('ls-lng')),
            address: sessionStorage.getItem('ls-address')
          }
        }))
        this.hideLoader()
      } else {
        this.hideLoader()
        this.handleErrorMessage()
      }
    }
  }
)

// ─── Calculate Yard Size ──────────────────────────────────────────────────────
customElements.get('calculate-yard-size') || customElements.define('calculate-yard-size',
  class extends HTMLElement {
    constructor() {
      super()
      this.map = null
      this.drawingManager = null
      this.polygons = []
      this.totalArea = 0
    }

    connectedCallback() {
      // Wait for Google Maps to be ready
      if (typeof google !== 'undefined') {
        this.initMap()
      } else {
        window.addEventListener('google-maps-ready', () => this.initMap(), { once: true })
      }

      // Listen for address selection
      document.addEventListener('placeChanged', (e) => this.handlePlaceChanged(e.detail))

      // "Change" button re-enables the search input
      this.querySelector('#ls-calculate-yard-size__address-change-button')
        ?.addEventListener('click', () => this.handleAddressChange())

      // Pre-fill from session if returning to page
      const savedAddress = sessionStorage.getItem('ls-address')
      const savedLat    = sessionStorage.getItem('ls-lat')
      const savedLng    = sessionStorage.getItem('ls-lng')

      if (savedAddress && savedLat && savedLng) {
        this.updateAddressDisplay(savedAddress)
        // Map not ready yet — defer centering until initMap runs
        this._pendingLocation = {
          lat: parseFloat(savedLat),
          lng: parseFloat(savedLng)
        }
      }
    }

    initMap() {
      this.map = new google.maps.Map(this.querySelector('#property-map-find-yarn'), {
        center: this._pendingLocation || { lat: 39.8283, lng: -98.5795 }, // US center default
        zoom: this._pendingLocation ? 19 : 5,
        mapTypeId: 'satellite',
        tilt: 0
      })

      this.initDrawingManager()

      if (this._pendingLocation) {
        this._pendingLocation = null
      }
    }

    initDrawingManager() {
      this.drawingManager = new google.maps.drawing.DrawingManager({
        drawingMode: google.maps.drawing.OverlayType.POLYGON,
        drawingControl: true,
        drawingControlOptions: {
          position: google.maps.ControlPosition.TOP_CENTER,
          drawingModes: [google.maps.drawing.OverlayType.POLYGON]
        },
        polygonOptions: {
          fillColor: '#00C853',
          fillOpacity: 0.35,
          strokeColor: '#00C853',
          strokeWeight: 2,
          clickable: true,
          editable: true,
          zIndex: 1
        }
      })

      this.drawingManager.setMap(this.map)

      google.maps.event.addListener(this.drawingManager, 'polygoncomplete', (polygon) => {
        this.polygons.push(polygon)
        this.recalculateArea()

        // Recalc when user edits the polygon
        polygon.getPath().addListener('set_at',    () => this.recalculateArea())
        polygon.getPath().addListener('insert_at',  () => this.recalculateArea())
        polygon.getPath().addListener('remove_at',  () => this.recalculateArea())
      })
    }

    handlePlaceChanged({ lat, lng, address }) {
      this.updateAddressDisplay(address)

      if (this.map) {
        this.map.setCenter({ lat, lng })
        this.map.setZoom(19)
      }
    }

    updateAddressDisplay(address) {
      // Populate the disabled "address you entered" input
      const displayInput = this.querySelector('.address-display #autocomplete')
      if (displayInput) displayInput.value = address

      // Show the address-display row, hide the search row (optional UX)
      // Uncomment if you want the swap behaviour:
      // this.querySelector('.search-row')?.classList.add('hidden')
      // this.querySelector('.address-display')?.classList.remove('hidden')
    }

    handleAddressChange() {
      // Clear session & reset display
      sessionStorage.removeItem('ls-lat')
      sessionStorage.removeItem('ls-lng')
      sessionStorage.removeItem('ls-address')

      const displayInput = this.querySelector('.address-display #autocomplete')
      if (displayInput) displayInput.value = ''

      // Focus the search input
      const searchInput = this.querySelector('.search-row #autocomplete')
      if (searchInput) {
        searchInput.value = ''
        searchInput.focus()
      }
    }

    recalculateArea() {
      this.totalArea = this.polygons.reduce((sum, polygon) => {
        return sum + google.maps.geometry.spherical.computeArea(polygon.getPath())
      }, 0)

      // Convert m² → sq ft  (1 m² = 10.7639 sq ft)
      const sqFt = Math.round(this.totalArea * 10.7639)

      const areaInput = this.querySelector('#calculate-yard-size__total-area')
      if (areaInput) areaInput.value = `${sqFt.toLocaleString()} sq. ft.`
    }
  }
)